import { useMemo } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import {
  FilterDropdown,
  type FilterDropdownOption,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/DashboardFilterDropdown';
import { institutionLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  selectHeldRoots,
  selectStoredOperatorRootId,
} from '@backrs_ui/grow-ui/src/redux/operatorContext';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

const { switcher, workingIn } = appStrings.modules.coachingDashboard.institutionAdmin;

type OperatorContextSwitcherProps = Readonly<{
  id?: string;
}>;

/**
 * Nothing renders for a caller with one root or none: their reads resolve without a context at
 * all. Nothing renders in platform mode either, which names no root of the caller's own; the
 * way out of that mode is the Operators tab.
 */
export const OperatorContextSwitcher = ({
  id = 'operator-context-switcher',
}: OperatorContextSwitcherProps) => {
  const heldRoots = useAppSelector(selectHeldRoots);
  const institutionId = useAppSelector(selectStoredOperatorRootId);
  const onSwitch = useOperatorInstitutionSwitch();

  const options = useMemo<FilterDropdownOption[]>(
    () => heldRoots.map((root) => ({ value: String(root.id), label: institutionLabel(root) })),
    [heldRoots],
  );

  if (options.length < 2 || institutionId === null) return null;

  // The control falls back to its own label when the selection is not among the options.
  const isSelectionHeld = options.some((option) => option.value === String(institutionId));

  return (
    <Box sx={containerSx} role="group" aria-label={switcher.ariaLabel}>
      <Typography variant="text-sm-medium" sx={labelSx}>
        {workingIn}
      </Typography>
      <FilterDropdown
        id={id}
        label={isSelectionHeld ? workingIn : switcher.unavailableOrganization}
        options={options}
        multi={false}
        isClearable={false}
        startIconName="Building08"
        selectedValue={isSelectionHeld ? String(institutionId) : null}
        onChange={(value) => {
          if (value !== null) onSwitch(Number(value));
        }}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  paddingY: backrs.spacing['spacing-md'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  whiteSpace: 'nowrap',
});
